// src/apiUtils/uplynkCentralTrackCallStatus.js
import ApiClient from '../services/ApiClient'; // Add this import for your ApiClient
import { resolveTemplateString } from "./objectUtils";
/**
 * Generalized job tracking function that polls an API endpoint until a job
 * reaches a target state or times out.
 * 
 * @param {string} trackingId - ID of the job to track
 * @param {Array<string>|string} targetStates - States that indicate job completion
 * @param {Object} options - Runtime options like credentials and callbacks
 * @param {Object} config - Configuration object for the tracker
 * @returns {Promise<Object>} - Final API response when job completes
 */
export const uplynkCentralTrackCallStatus = async (trackingId, targetStates, options = {}, config = {}) => {
  console.log("&&& - uplynkCentralTrackCallStatus");
  console.log("trackingId: ", trackingId);
  console.log("targetStates: ", targetStates);
  console.log("options: ", options);
  console.log("config: ", config);

  console.log(`Starting job tracking for ${trackingId}`);

  console.log("&&&&&&&&&&&&&&&&&&&");
  
  // Normalize targetStates to array
  const targetStateArray = Array.isArray(targetStates) ? targetStates : [targetStates];
  
  // Extract options with defaults
  const {
    credentials,                // User credentials (required)
    onProgress = null,          // Optional callback for progress updates
    onComplete = null,          // Optional callback for job completion
    abortController = new AbortController() // Accept external abort controller
  } = options;
  
  // Helper function to safely get a value from a nested path
  const getValueByPath = (obj, path) => {
    if (!obj || !path) return undefined;
    
    // Handle dot notation paths and convert to array
    const parts = typeof path === 'string' 
      ? path.replace(/\[(\w+)\]/g, '.$1').split('.') 
      : path;
    
    // Navigate through the object
    let current = obj;
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (part === '') continue; // Skip empty parts
      
      // Stop if we hit null/undefined or the property doesn't exist
      if (current === null || current === undefined || !(part in current)) {
        return undefined;
      }
      
      current = current[part];
    }
    
    return current;
  };
  
  // Merge configuration with defaults
  const apiConfig = {
    path: config.api?.path || '/api/jobs/status',
    method: config.api?.method || 'GET',
    rootUrl: config.api?.rootUrl || '',
    headers: config.api?.headers || {}
  };
  
  const requestConfig = {
    paramsFunction: config.request?.paramsFunction || ((id) => ({ id }))
  };
  
  const responseConfig = {
    statePath: config.response?.statePath || 'status',
    progressMapping: config.response?.progressMapping || {},
    completionMapping: config.response?.completionMapping || {}
  };
  
  const statesConfig = {
    completeStates: config.states?.completeStates || [],
    errorStates: config.states?.errorStates || ['error']
  };
  
  const pollingConfig = {
    maxAttempts: config.polling?.maxAttempts || 100,
    intervalMs: config.polling?.intervalMs || 5000,
    timeoutMs: config.polling?.timeoutMs || 10 * 60 * 1000,
    backoff: {
      enabled: config.polling?.backoff?.enabled ?? true,
      factor: config.polling?.backoff?.factor || 0.5,
      maxMs: config.polling?.backoff?.maxMs || 30000
    }
  };
  
  // All states that indicate job completion (target states + configured complete states)
  const allCompleteStates = [...new Set([...targetStateArray, ...statesConfig.completeStates])];
  
  // Create API client with the shared abort controller
  const client = new ApiClient(credentials, {
    timeout: 30000, // 30 seconds timeout for each individual request
    abortController: abortController,
    callbacks: {
      afterResponse: (data) => {
        if (onProgress) {
          // Base progress data
          const progressData = {
            response: data.response,
            attempt: attempts,
            maxAttempts: pollingConfig.maxAttempts
          };
          
          // Add mapped fields from response
          for (const [key, path] of Object.entries(responseConfig.progressMapping)) {
            const value = getValueByPath(data.response, path);
            if (value !== undefined) {
              progressData[key] = value;
            }
          }
          
          onProgress(progressData);
        }
      }
    }
  });
  
  // Configure the endpoint
  const endpoint = {
    path: apiConfig.path,
    method: apiConfig.method,
    root_url: apiConfig.rootUrl,
    params: requestConfig.paramsFunction(trackingId),
    headers: apiConfig.headers
  };
  
  // Start polling
  let attempts = 0;
  const startTime = Date.now();
  
  while (attempts < pollingConfig.maxAttempts) {
    // Check if aborted
    if (abortController.signal.aborted) {
      throw new Error('Job tracking was aborted');
    }
    
    attempts++;
    
    // Check for overall timeout
    if (Date.now() - startTime > pollingConfig.timeoutMs) {
      throw new Error(`Polling timed out after ${pollingConfig.timeoutMs}ms`);
    }
    
    try {
      // Make the API call
      const response = await client.executeApiCall(endpoint);
      console.log(`Poll attempt ${attempts}/${pollingConfig.maxAttempts}`);
      
      // Extract the job status from response
      const jobStatus = getValueByPath(response, responseConfig.statePath) || 'unknown';
      
      // Check if we've reached a target state
      if (allCompleteStates.includes(jobStatus)) {
        console.log(`Job reached target state: ${jobStatus}`);
        
        // Extract completion data if configured
        // if (onComplete) {
        //   // Extract data for completion handler
        //   const completionData = {};
        //   // for (const [key, path] of Object.entries(responseConfig.completionMapping)) {
        //   //   const value = getValueByPath(response, path);
        //   //   if (value !== undefined && value !== '') {
        //   //     completionData[key] = value;
        //   //   }
        //   // }
        //   for (const [key, pathOrTemplate] of Object.entries(responseConfig.completionMapping)) {
        //     if (typeof pathOrTemplate === 'string' && pathOrTemplate.includes('{')) {
        //       // Handle template string using imported function
        //       completionData[key] = resolveTemplateString(pathOrTemplate, {}, response);
        //     } else {
        //       // Handle regular path
        //       const value = getValueByPath(response, pathOrTemplate);
        //       if (value !== undefined && value !== '') {
        //         completionData[key] = value;
        //       }
        //     }
        //   }        
          
        //   // Pass both raw response and mapped data
        //   onComplete({
        //     ...response,
        //     extractedData: completionData
        //   });
        // }

        // Extract completion data if configured
    // Extract completion data if configured
    if (onComplete) {
      // Extract data for completion handler
      const completionData = {};
      console.log('uplynkCentralTrack - Completion mapping:', responseConfig.completionMapping);
      
      for (const [key, pathOrTemplate] of Object.entries(responseConfig.completionMapping)) {
        // Log each key-path pair being processed
        console.log('`uplynkCentralTrack` - Processing:', key, pathOrTemplate);
        
        // Check if this is a template string
        if (typeof pathOrTemplate === 'string' && pathOrTemplate.includes('{')) {
          console.log('uplynkCentralTrack - Template detected for', key);
          
          // Using your existing function to resolve templates
          const resolved = resolveTemplateString(pathOrTemplate, {}, response);
          console.log('uplynkCentralTrack - Template resolution result:', resolved);
          
          if (resolved !== pathOrTemplate) { // Only add if resolution was successful
            completionData[key] = resolved;
            console.log('uplynkCentralTrack - ✅ Resolution SUCCESSFUL:', key, '=', resolved);
          } else {
            console.log('uplynkCentralTrack - ❌ Resolution FAILED for', key);
          }
        } else {
          // Handle as a regular path
          console.log('uplynkCentralTrack - Regular path extraction for', key);
          const value = getValueByPath(response, pathOrTemplate);
          console.log('uplynkCentralTrack - Path extraction result:', value);
          
          if (value !== undefined && value !== '') {
            completionData[key] = value;
            console.log('uplynkCentralTrack - ✅ Path extraction SUCCESSFUL:', key, '=', value);
          } else {
            console.log('uplynkCentralTrack - ❌ Path extraction FAILED for', key);
          }
        }
      }
      
      console.log('uplynkCentralTrack - Final completionData:', completionData);
      
      // Pass both raw response and mapped data
      onComplete({
        ...response,
        extractedData: completionData
      });
    }
    return response;
      }
      
      // Check if we've reached an error state that isn't a target state
      if (statesConfig.errorStates.includes(jobStatus) && !allCompleteStates.includes(jobStatus)) {
        throw new Error(`Job failed with status: ${jobStatus}`);
      }
      
      // Check if aborted before waiting
      if (abortController.signal.aborted) {
        throw new Error('Job tracking was aborted');
      }
      
      // Wait before next poll - with abort handling
      await new Promise((resolve, reject) => {
        // Create timeout for interval
        const timeoutId = setTimeout(() => {
          // Remove the event listener when the timeout completes
          abortController.signal.removeEventListener('abort', abortHandler);
          resolve();
        }, pollingConfig.intervalMs);
        
        // Listen for abort signal during wait period
        const abortHandler = () => {
          clearTimeout(timeoutId);
          reject(new Error('Job tracking was aborted'));
        };
        
        // Add abort listener for this wait period
        abortController.signal.addEventListener('abort', abortHandler);
      });
      
    } catch (error) {
      // Check if this was an abort error
      if (abortController.signal.aborted || error.name === 'AbortError' || 
          error.message === 'Job tracking was aborted') {
        throw new Error('Job tracking was aborted');
      }
      
      console.error(`Poll attempt ${attempts} failed:`, error);
      
      // For network errors, we'll continue trying
      if (attempts < pollingConfig.maxAttempts) {
        // Check if aborted before waiting
        if (abortController.signal.aborted) {
          throw new Error('Job tracking was aborted');
        }
        
        // Calculate backoff time if enabled
        let backoffTime = pollingConfig.intervalMs;
        if (pollingConfig.backoff.enabled) {
          backoffTime = Math.min(
            pollingConfig.intervalMs * (1 + attempts * pollingConfig.backoff.factor),
            pollingConfig.backoff.maxMs
          );
        }
        
        // Exponential backoff - with abort handling
        await new Promise((resolve, reject) => {
          // Create timeout with cleanup in the callback
          const timeoutId = setTimeout(() => {
            // Remove the event listener when the timeout completes
            abortController.signal.removeEventListener('abort', abortHandler);
            resolve();
          }, backoffTime);
          
          // Listen for abort signal during backoff period
          const abortHandler = () => {
            clearTimeout(timeoutId);
            reject(new Error('Job tracking was aborted'));
          };
          
          // Add abort listener
          abortController.signal.addEventListener('abort', abortHandler);
        });
        
        continue;
      }
      
      throw error;
    }
  }
  
  throw new Error(`Polling exceeded maximum attempts (${pollingConfig.maxAttempts})`);
};

export default uplynkCentralTrackCallStatus;